import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject , of} from 'rxjs';
import { map, retry } from 'rxjs/operators';
import { USER_ROLES } from '../models/api-endpoints';
import { UserRoleDetails } from '../models/user-role-details';
import { AppConfig } from "../design/app";
import { IAppConfig } from '../core/models/app-config.model';
import { IApiResponse } from '../core/models/api-response.model';
import { MasterData } from '../models/master-model';
import { UserAssignedCountry } from '../models/user-assigned-country';
import { SessionStorageService } from '../core/services/session-storage.service';
import Collapse from 'bootstrap/js/dist/collapse';

@Injectable({
  providedIn: 'root',
})
export class UserRolesService {
  roleId: number = 0;
  assignedCountries: Array<any> = [];
  userId: number = 0;
  selectedRoleId = 0;
  loggedInEmail: ReplaySubject<string> = new ReplaySubject(1);
  roleIdSubject: ReplaySubject<number> = new ReplaySubject(1);
  loggedInUserIdSubject: ReplaySubject<number> = new ReplaySubject(1);
  disableList: ReplaySubject<boolean> = new ReplaySubject(1);
  userRoleList: ReplaySubject<any> = new ReplaySubject(1);
  navigationList: Subject<boolean> = new Subject();
  omnitureEnvironment: ReplaySubject<string> = new ReplaySubject(1);
  private isUserRegisteredSubject = new BehaviorSubject<boolean>(false);
  isUserRegistered$ = this.isUserRegisteredSubject.asObservable();
  private isInvalidDomainSubject = new BehaviorSubject<boolean>(false);
  isInvalidDomain$ = this.isInvalidDomainSubject.asObservable();

  appConfig:any;
  baseUrl;
  UserRoles;
  ISUSNonUs: boolean = false;

  constructor(
    private http: HttpClient,
    private sessionStorage: SessionStorageService
  ) {
    this.appConfig = AppConfig;
    this.baseUrl = this.appConfig.session.baseUrl;
  }

  closeAccordion(id) {
    const element = document.querySelector(id);
    if (element) {
      const myCollapse = new Collapse(element, {
        toggle: true,
      });
    }
  }
  set currentRoleId(id: number) {
    this.selectedRoleId = id;
  }
  setIsUserRegistered(value: boolean): void {
    this.isUserRegisteredSubject.next(value);
  }
  setIsInvalidDomain(value: boolean): void {
    this.isInvalidDomainSubject.next(value);
  }
  get currentRoleId() {
    return this.selectedRoleId;
  }

  set userRoleId(id: number) {
    this.roleId = id;
  }

  get userRoleId() {
    return this.roleId;
  }

  set userAssignedCountries(countryList: Array<any>) {
    this.assignedCountries = countryList;
  }

  get userAssignedCountries() {
    return this.assignedCountries;
  }

  set loggedInUserId(id: number) {
    this.userId = id;
  }

  get loggedInUserId() {
    return this.userId;
  }

  setLoggedInUserEmail(data: string) {
    this.loggedInEmail.next(data);
  }

  getLoggedInUserEmail(): Observable<string> {
    return this.loggedInEmail.asObservable();
  }
  setOmnitureEnvironment(data: string) {
    this.omnitureEnvironment.next(data);
  }

  getOmnitureEnvironment(): Observable<string> {
    return this.omnitureEnvironment.asObservable();
  }

  setUserRoleID(data: number) {
    this.roleIdSubject.next(data);
  }

  getUserRoleID(): Observable<number> {
    return this.roleIdSubject.asObservable();
  }

  setLoggedInUserID(data: number) {
    this.loggedInUserIdSubject.next(data);
  }

  getLoggedInUserID(): Observable<number> {
    return this.loggedInUserIdSubject.asObservable();
  }

  setDisableUserRoleList(data: boolean) {
    this.disableList.next(data);
  }

  getDisableUserRoleList(): Observable<boolean> {
    return this.disableList.asObservable();
  }
  setUserRoleList(data: any) {
    const userRoles = this.updateUserRoleNames(data);
    this.userRoleList.next(userRoles);
  }

  getUserRoleList(): Observable<any> {
    return this.userRoleList.asObservable();
  }

  setLeftNavigationProperty() {
    this.navigationList.next(false);
  }

  getNavigationProperty(): Observable<any> {
    return this.navigationList.asObservable();
  }

  getUserRoles(): Observable<UserRoleDetails | null> {
    const endpoint = this.appConfig.session.getLoggedInUserRoleList;
    const apiUrl = `${this.baseUrl + endpoint}`;
    return this.http.get<IApiResponse<UserRoleDetails>>(apiUrl).pipe(
      retry(3), // Retry once on error
      map((res: IApiResponse<UserRoleDetails>) => {
        // Check for status code 200
        if (res && res.Success == true && Object.keys(res).length > 0) {
          this.sessionStorage.setItem('HighestRoleId', res.Data.HighestRoleId);
          this.UserRoles = res.Data;
          this.ISUSNonUs = res.Data.ISUSNonUs;
          return res.Data;
        }
        return null;
      })
    );
  }

  updateUserRole(selectedRole: number): Observable<any> {
    const endpoint = this.appConfig.session.updateHighestUserRole;
    const apiUrl = `${this.baseUrl + endpoint}`;
    return this.http
      .post<IApiResponse<UserAssignedCountry>>(apiUrl, selectedRole)
      .pipe(
        map((res: any) => {
          if (res) {
            return res.Data;
          }
          return null;
        })
      );
  }

  getUpdatedMasterData(): Observable<any> {
    const endpoint = this.appConfig.session.getMaterData;
    const apiUrl = `${this.baseUrl + endpoint}`;
    const headers = new HttpHeaders();
    return this.http
      .get<IApiResponse<MasterData>>(
        apiUrl + '?returnDeloittedata=true&IsIncludeNA=true',
        { headers: headers }
      )
      .pipe(
        map((res) => {
          return res.Data;
        })
      );
  }

  updateUserRoleNames(roles: any) {
    const userRole = {
      1: 'Administrator',
      2: 'Facilitator',
      3: 'Country Administrator',
      4: 'Coordinator',
      5: 'GX Facilitator',
      6: 'Subscription Administrator',
      8: 'XA (non-GX)',
    };
    const userRoleDetails: { roleId: number; roleName: any }[] = [];
    if (roles && roles.length > 0) {
      roles.map((role: any) => {
        userRoleDetails.push({ roleId: role, roleName: userRole[role] });
      });
    }
    return userRoleDetails;
  }
}
